import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { FileStack, Loader2, Users2, Save } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { plansApi, PageShell, PageHeader, StatusBadge, CARD, PRIMARY, GHOST, STATUS, inr, dateShort, errMsg } from './shared';

const AgenciesDialog = ({ open, onOpenChange, onSaved }) => {
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) plansApi.get('/brand/agencies').then((d) => setList(d.agencies)).catch(() => setList([])); }, [open]);
  const save = async () => {
    setBusy(true);
    try { await plansApi.put('/brand/agencies', { ids: list.filter((a) => a.linked).map((a) => a.id) }); toast.success('Agency access updated'); onSaved(); onOpenChange(false); } catch (e) { toast.error(errMsg(e, 'Could not save')); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="brand-agencies-dialog">
        <DialogHeader><DialogTitle>Agencies that can submit plans</DialogTitle><DialogDescription>Only ticked agencies can send you pitch decks and creator sheets.</DialogDescription></DialogHeader>
        {!list ? <Loader2 className="animate-spin" size={16} /> : (
          <div className="space-y-2">
            {list.map((a) => (
              <label key={a.id} className="flex items-center gap-3 rounded-xl border border-black/[0.08] px-4 py-3 cursor-pointer hover:border-[#FF451A]/40 transition-colors" data-testid={`brand-agency-option-${a.id}`}>
                <input type="checkbox" checked={a.linked} onChange={() => setList(list.map((x) => (x.id === a.id ? { ...x, linked: !x.linked } : x)))} className="accent-[#FF451A]" data-testid={`brand-agency-check-${a.id}`} />
                <span className="text-sm font-medium">{a.agency_name}</span>
              </label>
            ))}
            {!list.length && <p className="text-sm text-[#6B6B6B]">No agencies on Vyvre yet.</p>}
            <div className="flex justify-end pt-2"><button onClick={save} disabled={busy} className={PRIMARY} data-testid="brand-agencies-save">{busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save</button></div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const PlanCard = ({ p, i }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}>
    <Link to={`/brand/agency-plans/${p.id}`} className={`${CARD} p-5 flex flex-col gap-4 hover:border-[#FF451A]/40 hover:-translate-y-0.5 transition-[transform,border-color]`} data-testid={`brand-plan-card-${p.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-semibold uppercase tracking-[0.1em] text-[#FF451A]">{p.agency_name}</div>
        <StatusBadge status={p.status} testid={`brand-plan-status-${p.id}`} />
      </div>
      <div className="font-semibold text-lg leading-snug text-[#0F0F0F] dark:text-white">{p.campaign_name}</div>
      <div className="flex items-end justify-between">
        <div><div className="text-[10px] uppercase tracking-wider text-[#6B6B6B]">Total budget</div><div className="font-mono font-bold text-[#0F0F0F] dark:text-white" data-testid={`brand-plan-budget-${p.id}`}>{inr(p.total_budget)}</div></div>
        <div className="text-right text-xs text-[#6B6B6B]">{p.creator_count} creators<br />submitted {dateShort(p.submitted_at)}</div>
      </div>
    </Link>
  </motion.div>
);

export default function BrandAgencyPlans() {
  const [plans, setPlans] = useState(null);
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const load = useCallback(() => { plansApi.get('').then((d) => setPlans(d.plans)).catch((e) => { setPlans([]); toast.error(errMsg(e, 'Could not load plans')); }); }, []);
  useEffect(load, [load]);
  const shown = (plans || []).filter((p) => filter === 'all' || p.status === filter);
  return (
    <PageShell testid="brand-agency-plans-page">
      <PageHeader icon={FileStack} title="Agency Plans" sub="Every pitch your agencies have submitted, in one place." action={<button onClick={() => setOpen(true)} className={GHOST} data-testid="brand-manage-agencies-btn"><Users2 size={12} /> Manage agencies</button>} />
      <div className="flex gap-2 flex-wrap" data-testid="brand-plan-filters">
        {['all', 'under_review', 'changes_requested', 'approved'].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${filter === s ? 'bg-[#FF451A] text-white' : 'bg-black/[0.04] text-[#4B5563] hover:bg-[#FF451A]/10'}`} data-testid={`brand-plan-filter-${s}`}>
            {s === 'all' ? 'All' : STATUS[s].label} {plans && <span className="opacity-70">· {s === 'all' ? plans.length : plans.filter((p) => p.status === s).length}</span>}
          </button>
        ))}
      </div>
      {plans === null ? <Loader2 className="animate-spin" size={16} /> : !shown.length
        ? <div className={`${CARD} p-8 text-center text-sm text-[#6B6B6B]`} data-testid="brand-plans-empty">No plans here yet.</div>
        : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">{shown.map((p, i) => <PlanCard key={p.id} p={p} i={i} />)}</div>}
      <AgenciesDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </PageShell>
  );
}
